/**
 * Accessibility Widget (edlio/cms#16726). Loaded with `defer` at the end of
 * every public page where the school turned the widget on; see
 * AccessibilityWidgetEmbed.java for when that is.
 *
 * Plain TypeScript, no React: this runs for every visitor on every page
 * (ticket §2, D6). The folder is called `react/` only because the modern-ui
 * webpack config discovers entries there.
 */
import { applyPrefs, type Environment } from './apply';
import { HOST_ID } from './contract';
import { loadPrefs, onExternalChange, savePrefs, type Prefs } from './prefs';
import { Widget } from './widget';

declare global {
  interface Window {
    __edlioA11yWidgetLoaded?: boolean;
  }
}

function boot(): void {
  // Idempotent: a design that includes the footer twice must not get two widgets.
  if (window.__edlioA11yWidgetLoaded) return;
  // The server puts the real host at the very end of <body>, after all page
  // content. Page content could carry an element with the same id (a pasted
  // embed, an edited page), and getElementById would return that one first,
  // so take the last match.
  const hosts = document.querySelectorAll<HTMLElement>(`div#${HOST_ID}`);
  const host = hosts.length ? hosts[hosts.length - 1] : null;
  if (!host) return;
  window.__edlioA11yWidgetLoaded = true;

  const motionQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
  const env = (): Environment => ({
    deviceReducesMotion: !!(motionQuery && motionQuery.matches),
  });

  let prefs: Prefs = loadPrefs();

  const commit = (next: Prefs, persist: boolean) => {
    prefs = next;
    if (persist) savePrefs(next);
    applyPrefs(next, env());
    widget.update(next, env());
  };

  const widget = new Widget(host, (next) => commit(next, true));
  applyPrefs(prefs, env());
  if (!widget.mount(prefs, env())) return;

  // Another tab changed the settings.
  onExternalChange((next) => commit(next, false));

  // The device's reduce-motion setting changed while the page is open.
  if (motionQuery) {
    const onMotionChange = () => commit(prefs, false);
    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', onMotionChange);
    } else if (typeof motionQuery.addListener === 'function') {
      motionQuery.addListener(onMotionChange); // Safari 13
    }
  }
}

function safeBoot(): void {
  try {
    boot();
  } catch (e) {
    // Never break the school's page. The pre-paint snippet has already applied
    // the CSS-only settings, so the visitor keeps those.
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', safeBoot);
} else {
  safeBoot();
}
